import React from "react";
import { TrashIcon, PlusIcon, PencilIcon } from "@heroicons/react/24/outline";
import { BuilderSplitRow } from "../BuilderSplitShared/types";
import DeductionCommentBadge from "../BuilderSplitShared/DeductionCommentBadge";

interface BuilderOption {
  builderId: number;
  builderEmail: string;
  builderName: string;
  builderNumber: number;
  employeeNumber: number;
}

interface BuilderSplitTableProps {
  rows: BuilderSplitRow[];
  allBuilders: BuilderOption[];
  canEdit: boolean;
  onBuilderChange: (index: number, employeeNumber: number) => void;
  onSplitChange: (index: number, split: number) => void;
  onDeductionChange: (index: number, amount: number) => void;
  onEditDeductionComment: (index: number) => void;
  onRemoveRow: (index: number) => void;
  onAddRow: () => void;
}

const BuilderSplitTable: React.FC<BuilderSplitTableProps> = ({
  rows,
  allBuilders,
  canEdit,
  onBuilderChange,
  onSplitChange,
  onDeductionChange,
  onEditDeductionComment,
  onRemoveRow,
  onAddRow,
}) => {
  return (
    <div className="mt-2">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Builder
              </th>
              <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Split (%)
              </th>
              <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Deduction
              </th>
              <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                Explanation
              </th>
              {canEdit && (
                <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              )}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200 bg-white">
            {rows.map((row, index) => (
              <tr key={`${row.employeeNumber || "new"}-${index}`}>
                <td className="whitespace-nowrap px-6 py-4 align-top">
                  <select
                    value={row.employeeNumber}
                    onChange={(e) => onBuilderChange(index, Number(e.target.value))}
                    disabled={!canEdit}
                    className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  >
                    <option value="">Select Builder</option>
                    {allBuilders.map((builder) => (
                      <option key={builder.employeeNumber} value={builder.employeeNumber}>
                        {`${builder.builderName} (${builder.builderEmail})`}
                      </option>
                    ))}
                  </select>
                </td>

                <td className="whitespace-nowrap px-6 py-4 align-top">
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={row.split}
                      onChange={(e) => onSplitChange(index, parseInt(e.target.value || "0", 10))}
                      disabled={!canEdit}
                      className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-10 text-base focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-500"
                    />
                    <span className="ml-2 text-gray-500">%</span>
                  </div>
                </td>

                <td className="whitespace-nowrap px-6 py-4 align-top">
                  <div className="flex items-center">
                    <span className="mr-1 text-gray-500">$</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={row.deductionAmount || 0}
                      onChange={(e) =>
                        onDeductionChange(index, parseFloat(e.target.value || "0"))
                      }
                      disabled={!canEdit}
                      className="mt-1 block w-full rounded-md border-gray-300 py-2 pl-3 pr-3 text-base focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-500"
                    />
                  </div>
                </td>

                <td className="px-6 py-4 align-top">
                  {(row.deductionAmount || 0) > 0 ? (
                    <div className="flex items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <DeductionCommentBadge comment={row.deductionComment} />
                      </div>

                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => onEditDeductionComment(index)}
                          className="mt-1 text-amber-600 hover:text-amber-800"
                          title="Edit Deduction Explanation"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">—</span>
                  )}
                </td>

                {canEdit && (
                  <td className="whitespace-nowrap px-6 py-4 align-top">
                    <button
                      onClick={() => onRemoveRow(index)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {canEdit && rows.length < allBuilders.length && (
        <button
          onClick={onAddRow}
          className="mt-4 inline-flex items-center rounded-md border border-transparent bg-primary-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
          Add Builder
        </button>
      )}
    </div>
  );
};

export default BuilderSplitTable;